import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { MANAGEMENT_ROLES } from '../constants/management';
import { useAppSelector } from '../store/hooks';
import { CriticalStockNotification, InAppNotification } from '../types';
import { normalizeRoleNames } from '../utils/roleNames';
import { emitToast } from '../utils/toastEvents';
import './NotificationBell.css';

interface NotificationResponse {
  notifications: InAppNotification[];
  unreadCount: number;
  nextBeforeId?: number | null;
}

const requestOptions = { timeout: 30_000 };

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { token, roles, permissions } = useAppSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [criticalAlerts, setCriticalAlerts] = useState<CriticalStockNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inboxError, setInboxError] = useState('');
  const [criticalError, setCriticalError] = useState('');
  const [inboxLoading, setInboxLoading] = useState(false);
  const [criticalLoading, setCriticalLoading] = useState(false);
  const [reading, setReading] = useState(false);
  const [inboxUpdatedAt, setInboxUpdatedAt] = useState<string | null>(null);
  const [criticalUpdatedAt, setCriticalUpdatedAt] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const refreshVersionRef = useRef(0);
  const criticalVersionRef = useRef(0);
  const inboxBusyRef = useRef(false);
  const criticalBusyRef = useRef(false);
  const readingIdsRef = useRef(new Set<number>());
  const markingAllRef = useRef(false);
  const expandedRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const previousNotificationIdsRef = useRef<Set<number> | null>(null);
  const previousCriticalIdsRef = useRef<Set<number> | null>(null);
  const invalidateInbox = useCallback(() => {
    ++refreshVersionRef.current;
    inboxBusyRef.current = false;
  }, []);
  const invalidateCritical = useCallback(() => {
    ++criticalVersionRef.current;
    criticalBusyRef.current = false;
  }, []);
  const normalizedRoles = normalizeRoleNames(roles);
  const canReadCriticalStock = permissions.includes('page.management.inventory-balance')
    || MANAGEMENT_ROLES.some((role) => normalizedRoles.includes(role));

  const refreshInbox = useCallback(async (showNewToast: boolean, beforeId?: number) => {
    if (!token || inboxBusyRef.current || markingAllRef.current || readingIdsRef.current.size > 0) return;
    inboxBusyRef.current = true;
    setInboxLoading(true);
    const version = ++refreshVersionRef.current;
    try {
      const params = new URLSearchParams();
      if (unreadOnly) params.set('unreadOnly', 'true');
      if (beforeId !== undefined) params.set('beforeId', String(beforeId));
      const query = params.toString();
      const notificationResponse = await api.get<NotificationResponse>(`/notifications${query ? `?${query}` : ''}`, requestOptions);
      if (version !== refreshVersionRef.current) return;
      const nextNotifications = notificationResponse.data.notifications;
      const oldNotificationIds = previousNotificationIdsRef.current;

      if (showNewToast && !beforeId && oldNotificationIds) {
        nextNotifications
          .filter((item) => !item.readAtUtc && !oldNotificationIds.has(item.id))
          .slice(0, 3)
          .forEach((item) => emitToast({ kind: 'info', title: item.title, message: item.message }));
      }
      if (beforeId === undefined) {
        previousNotificationIdsRef.current = new Set(nextNotifications.map((item) => item.id));
        setNotifications(nextNotifications);
        expandedRef.current = false;
      } else {
        setNotifications((items) => {
          const ids = new Set(items.map((item) => item.id));
          return [...items, ...nextNotifications.filter((item) => !ids.has(item.id))];
        });
        expandedRef.current = true;
      }
      setNextBeforeId(notificationResponse.data.nextBeforeId ?? null);
      setUnreadCount(notificationResponse.data.unreadCount);
      setInboxUpdatedAt(new Date().toISOString());
      setInboxError('');
    } catch (error) {
      if (version !== refreshVersionRef.current) return;
      console.error('Не вдалося оновити повідомлення.', error);
      setInboxError('Не вдалося оновити повідомлення. Попередні дані збережено.');
    } finally {
      if (version === refreshVersionRef.current) {
        inboxBusyRef.current = false;
        setInboxLoading(false);
      }
    }
  }, [token, unreadOnly]);

  const refreshCritical = useCallback(async (showNewToast: boolean) => {
    if (!token || !canReadCriticalStock || criticalBusyRef.current || markingAllRef.current) return;
    criticalBusyRef.current = true;
    setCriticalLoading(true);
    const version = ++criticalVersionRef.current;
    try {
      const response = await api.get<{ alerts: CriticalStockNotification[] }>('/notifications/critical-stock', requestOptions);
      if (version !== criticalVersionRef.current) return;
      const nextAlerts = response.data.alerts;
      const oldCriticalIds = previousCriticalIdsRef.current;
      if (showNewToast && oldCriticalIds) {
        const newlyCritical = nextAlerts.filter((item) => !oldCriticalIds.has(item.productId));
        if (newlyCritical.length > 0) {
          emitToast({
            kind: 'error',
            title: 'Критичний залишок',
            message: newlyCritical.length === 1
              ? `${newlyCritical[0].productName}: ${newlyCritical[0].balance} ${newlyCritical[0].weightType}`
              : `Критичного рівня досягли ще ${newlyCritical.length} товарів.`,
          });
        }
      }

      previousCriticalIdsRef.current = new Set(nextAlerts.map((item) => item.productId));
      setCriticalAlerts(nextAlerts);
      setCriticalUpdatedAt(new Date().toISOString());
      setCriticalError('');
    } catch (error) {
      if (version !== criticalVersionRef.current) return;
      console.error('Не вдалося оновити критичні залишки.', error);
      setCriticalError('Не вдалося оновити критичні залишки. Попередні дані збережено.');
    } finally {
      if (version === criticalVersionRef.current) {
        criticalBusyRef.current = false;
        setCriticalLoading(false);
      }
    }
  }, [canReadCriticalStock, token]);

  useEffect(() => {
    setNotifications([]);
    setNextBeforeId(null);
    setInboxUpdatedAt(null);
    setInboxError('');
    setInboxLoading(false);
    previousNotificationIdsRef.current = null;
    expandedRef.current = false;
    if (token && document.visibilityState !== 'hidden') void refreshInbox(false);
    return invalidateInbox;
  }, [invalidateInbox, refreshInbox, token]);

  useEffect(() => {
    setCriticalAlerts([]);
    setCriticalUpdatedAt(null);
    setCriticalError('');
    setCriticalLoading(false);
    previousCriticalIdsRef.current = null;
    if (token && document.visibilityState !== 'hidden') void refreshCritical(false);
    return invalidateCritical;
  }, [invalidateCritical, refreshCritical, token]);

  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      setOpen(false);
      return;
    }
    const refreshVisible = () => {
      if (document.visibilityState === 'hidden') return;
      // Keep the reading position while the user is browsing older pages.
      if (!openRef.current || !expandedRef.current) void refreshInbox(true);
      void refreshCritical(true);
    };
    const timer = window.setInterval(refreshVisible, 60_000);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [refreshInbox, refreshCritical, token]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (event.target instanceof Node && !panelRef.current?.contains(event.target))
        setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    dialogRef.current?.focus();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  const markRead = async (notification: InAppNotification) => {
    if (notification.readAtUtc || readingIdsRef.current.has(notification.id) || markingAllRef.current) return;
    readingIdsRef.current.add(notification.id);
    setReading(true);
    invalidateInbox();
    setInboxLoading(false);
    try {
      await api.put(`/notifications/${notification.id}/read`, undefined, requestOptions);
      setNotifications((items) => unreadOnly
        ? items.filter((item) => item.id !== notification.id)
        : items.map((item) => item.id === notification.id
          ? { ...item, readAtUtc: new Date().toISOString() }
          : item));
      setUnreadCount((count) => Math.max(0, count - 1));
    } finally {
      readingIdsRef.current.delete(notification.id);
      setReading(readingIdsRef.current.size > 0);
    }
  };

  const markAllRead = async () => {
    if (markingAllRef.current || readingIdsRef.current.size > 0) return;
    markingAllRef.current = true;
    invalidateInbox();
    invalidateCritical();
    setInboxLoading(false);
    setCriticalLoading(false);
    setLoading(true);
    setErrorMessage('');
    try {
      await api.put('/notifications/read-all', { criticalProductIds: criticalAlerts.map((item) => item.productId) }, requestOptions);
      ++refreshVersionRef.current;
      const readAtUtc = new Date().toISOString();
      setNotifications((items) => unreadOnly ? [] : items.map((item) => ({ ...item, readAtUtc: item.readAtUtc || readAtUtc })));
      setUnreadCount(0);
      setCriticalAlerts([]);
      markingAllRef.current = false;
      void refreshInbox(false);
      void refreshCritical(false);
    } catch (error) {
      console.error('Не вдалося позначити сповіщення прочитаними.', error);
      setErrorMessage('Не вдалося прочитати всі сповіщення. Повторіть спробу.');
    } finally {
      markingAllRef.current = false;
      setLoading(false);
    }
  };

  const openNotification = (notification: InAppNotification) => {
    const reminderOrderId = /^event-short:\d{8}:(\d+)$/.exec(notification.type)?.[1];
    const link = reminderOrderId
      ? `/management/order-supply?eventOrderId=${reminderOrderId}`
      : notification.link;
    setOpen(false);
    if (link) navigate(link);
    void markRead(notification).catch((error) => {
      console.error('Не вдалося позначити сповіщення прочитаним.', error);
      emitToast({ kind: 'error', title: 'Сповіщення не позначено прочитаним',
        message: 'Сторінку відкрито. Спробуйте позначити сповіщення прочитаним пізніше.' });
    });
  };

  if (!token) return null;
  const badgeCount = unreadCount + criticalAlerts.length;

  return (
    <div className="notification-bell" ref={panelRef}>
      <button
        type="button"
        ref={triggerRef}
        className="notification-bell__trigger"
        aria-label={badgeCount ? `Сповіщення, непрочитаних: ${badgeCount}` : 'Сповіщення'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
        </svg>
        {badgeCount > 0 && <span className="notification-bell__badge">{badgeCount > 99 ? '99+' : badgeCount}</span>}
      </button>

      {open && (
        <section className="notification-panel" ref={dialogRef} tabIndex={-1} role="dialog" aria-label="Центр сповіщень">
          <header className="notification-panel__header">
            <strong>Сповіщення</strong>
            <button type="button" onClick={() => void markAllRead()} disabled={loading || reading || badgeCount === 0}>
              Прочитати всі
            </button>
          </header>
          {errorMessage && <p role="alert" className="notification-panel__empty">{errorMessage}</p>}
          <div className="notification-panel__toolbar" aria-label="Фільтр повідомлень">
            <button type="button" aria-pressed={!unreadOnly} disabled={loading || reading} onClick={() => setUnreadOnly(false)}>Усі (історія)</button>
            <button type="button" aria-pressed={unreadOnly} disabled={loading || reading} onClick={() => setUnreadOnly(true)}>Непрочитані</button>
          </div>
          {permissions.includes('page.management.event-orders') &&
            <Link className="notification-panel__supply-link" to="/management/order-supply" onClick={() => setOpen(false)}>
              Забезпечення замовлень → зведений список закупівлі
            </Link>}

          {canReadCriticalStock && (
            <div className="notification-panel__section">
              <h3>Критичні залишки ({criticalAlerts.length})</h3>
              {criticalError && <p role="alert" className="notification-panel__empty">{criticalError}</p>}
              <button type="button" className="notification-panel__refresh" disabled={criticalLoading || loading}
                onClick={() => void refreshCritical(false)}>Оновити залишки</button>
              {criticalUpdatedAt && <p className="notification-panel__updated">Залишки оновлено: <time dateTime={criticalUpdatedAt}>{new Date(criticalUpdatedAt).toLocaleTimeString('uk-UA')}</time></p>}
              {criticalLoading && <p role="status" className="notification-panel__empty">Оновлення залишків…</p>}
              {criticalAlerts.length > 0 && <p className="notification-panel__empty">«Прочитати всі» приховає ці попередження до завтра, але не змінить залишки.</p>}
              {criticalAlerts.map((alert) => (
                <button
                  type="button"
                  className="notification-item notification-item--critical"
                  key={alert.productId}
                  onClick={() => { setOpen(false); navigate('/management/inventory-balance'); }}
                >
                  <strong>{alert.productName}</strong>
                  <span>
                    Залишок: {alert.balance} {alert.weightType}; поріг: {alert.criticalStockThreshold} {alert.weightType}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="notification-panel__section">
            <h3>Повідомлення та нагадування</h3>
            {inboxError && <p role="alert" className="notification-panel__empty">{inboxError}</p>}
            <button type="button" className="notification-panel__refresh" disabled={inboxLoading || loading || reading}
              onClick={() => void refreshInbox(false)}>Оновити повідомлення</button>
            {inboxUpdatedAt && <p className="notification-panel__updated">Повідомлення оновлено: <time dateTime={inboxUpdatedAt}>{new Date(inboxUpdatedAt).toLocaleTimeString('uk-UA')}</time></p>}
            {inboxLoading && <p role="status" className="notification-panel__empty">Завантаження повідомлень…</p>}
            {notifications.length === 0
              ? !inboxLoading && !inboxError && <p className="notification-panel__empty">{unreadOnly ? 'Непрочитаних повідомлень немає.' : 'Повідомлень немає.'}</p>
              : notifications.map((notification) => (
                <button
                  type="button"
                  className={`notification-item ${notification.readAtUtc ? '' : 'notification-item--unread'}`}
                  key={notification.id}
                  onClick={() => void openNotification(notification)}
                >
                  <strong>{notification.title}</strong>
                  <span>{notification.message}</span>
                  {notification.type.startsWith('event-short:') && <span className="notification-item__action">Переглянути забезпечення →</span>}
                  <time dateTime={notification.createdAtUtc}>
                    {new Date(notification.createdAtUtc).toLocaleString('uk-UA')}
                  </time>
                </button>
              ))}
            {nextBeforeId !== null && <button type="button" className="notification-panel__refresh"
              disabled={inboxLoading || loading || reading} onClick={() => void refreshInbox(false, nextBeforeId)}>Завантажити ще</button>}
            {expandedRef.current && <p className="notification-panel__empty">Перегляд історії: список не оновлюється автоматично, поки панель відкрита. «Оновити повідомлення» поверне до найновіших.</p>}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationBell;
